import type { PagesConfig } from '@shared/types/PagesConfig';
import type { ConsentTool } from '@/Integrations/Consent/types';
import { loadExternalScript, runInlineScriptOnce } from '@/Integrations/Shared/scriptLoader';

const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/i;
const GA_ID_PATTERN = /^(G-[A-Z0-9]+|UA-\d+-\d+)$/i;

function sanitizeJsString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function getGoogleToolIds(config: PagesConfig): { gaIds: string[]; gtmIds: string[] } {
  const gaConfig = config.widget_settings?.google_analytics;

  if (!gaConfig?.enabled || !gaConfig.widget_id_list?.length) {
    return { gaIds: [], gtmIds: [] };
  }

  const allIds = gaConfig.widget_id_list.map((id) => id.trim()).filter(Boolean);
  if (!allIds.length) {
    return { gaIds: [], gtmIds: [] };
  }

  return {
    gtmIds: allIds.filter((id) => GTM_ID_PATTERN.test(id)),
    gaIds: allIds.filter((id) => GA_ID_PATTERN.test(id)),
  };
}

function buildGoogleAnalyticsTool(config: PagesConfig): ConsentTool | null {
  const { gaIds } = getGoogleToolIds(config);
  const primaryGaId = gaIds[0];

  if (!primaryGaId) {
    return null;
  }

  return {
    id: 'google-analytics',
    requiredConsent: ['analytics'],
    async load() {
      await loadExternalScript({
        key: `tool:ga:library:${primaryGaId}`,
        src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryGaId)}`,
      });

      runInlineScriptOnce(
        'tool:ga:bootstrap',
        "window.dataLayer=window.dataLayer||[];window.gtag=window.gtag||function(){window.dataLayer.push(arguments);};window.gtag('js',new Date());",
      );

      gaIds.forEach((measurementId) => {
        const safeId = sanitizeJsString(measurementId);
        runInlineScriptOnce(
          `tool:ga:config:${measurementId}`,
          `window.gtag('config','${safeId}',{anonymize_ip:true});window['ga-disable-${safeId}']=false;`,
        );
      });
    },
    unload() {
      gaIds.forEach((measurementId) => {
        (window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = true;
      });
    },
  };
}

function buildGoogleTagManagerTool(config: PagesConfig): ConsentTool | null {
  const { gtmIds } = getGoogleToolIds(config);

  if (!gtmIds.length) {
    return null;
  }

  return {
    id: 'google-tag-manager',
    requiredConsent: ['marketing'],
    load() {
      gtmIds.forEach((gtmId) => {
        const safeId = sanitizeJsString(gtmId);
        runInlineScriptOnce(
          `tool:gtm:bootstrap:${gtmId}`,
          `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${safeId}');`,
        );
      });
    },
  };
}

export function buildConsentAwareTools(config: PagesConfig): ConsentTool[] {
  const toolBuilders = [buildGoogleAnalyticsTool, buildGoogleTagManagerTool];

  return toolBuilders
    .map((builder) => builder(config))
    .filter((tool): tool is ConsentTool => tool !== null);
}
